import { Link } from "@tanstack/react-router";
import { Phone, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { AnimatedSection } from "@/components/AnimatedSection";

interface Photo {
  url: string;
  alt: string;
}

interface Props {
  photos?: Photo[];
}

/**
 * "Not Your Average Independent Garage" — prestige proof callout.
 * Dark band with a 2×2 photo grid of premium workshop photos to
 * differentiate Woodseer from chain/dealer alternatives.
 */
export function PrestigeCallout({ photos = [] }: Props) {
  const biz = getBusinessConfig();
  const tiles = photos.slice(0, 4);

  return (
    <AnimatedSection>
      <section
        aria-labelledby="prestige-heading"
        className="section-y bg-foreground text-background border-t-2 border-primary"
      >
        <div className="mx-auto max-w-6xl px-4 md:px-6 grid lg:grid-cols-[3fr_2fr] gap-10 lg:gap-14 items-center">
          <div>
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
              Prestige &amp; performance
            </p>
            <h2 id="prestige-heading" className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">
              Not your average independent garage.
            </h2>
            <p className="mt-5 text-background/80 leading-relaxed max-w-xl">
              We've worked on everything from fleet vans to Bentleys, family hatches to McLarens.
              Our workshop has the equipment, the experience, and the attention to detail that
              prestige vehicles demand — at independent garage pricing.
            </p>
            <p className="mt-3 text-background/80 leading-relaxed max-w-xl">
              If you've ever been told an independent can't handle your car, bring it to us.
              We'll give you a straight answer before any work starts.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "prestige_callout" })}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Phone className="h-4 w-4" />
                Call to discuss your vehicle
              </a>
              <Link
                to="/premium-gallery"
                onClick={() => trackEvent("gallery_open", { location: "prestige_callout", target: "premium_gallery" })}
                className="inline-flex items-center gap-2 rounded-lg border border-background/30 px-5 py-3 text-sm font-bold text-background hover:bg-background/10 transition-colors"
              >
                See prestige &amp; performance gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {tiles.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {tiles.map((p, i) => (
                <div
                  key={i}
                  className="aspect-square overflow-hidden rounded-xl border border-background/10"
                >
                  <img
                    src={p.url}
                    alt={p.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </AnimatedSection>
  );
}
